import React from "react";
import { Reveal, StaggerGroup, StaggerItem } from "../components/Reveal";
import { PageHeader } from "./About";
import { useLang } from "../contexts/LanguageContext";
import { t } from "../i18n/translations";
import useSEO from "../hooks/useSEO";

/**
 * Neutral square photo placeholder (rounded corners, brand-tinted block + initials).
 * No random stock portraits per design brief.
 */
const PhotoPlaceholder = ({ name }) => {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
  return (
    <div className="relative aspect-square w-full overflow-hidden rounded-xl bg-gradient-to-br from-[#EEF2F8] via-[#E4EAF4] to-[#D8E0EC] border border-slate-200/80">
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="font-heading text-4xl sm:text-5xl font-bold tracking-wide text-[#163B71]/35 select-none">
          {initials}
        </span>
      </div>
    </div>
  );
};

/** Card used for Founding + Executive sections (and as person-card style for Leadership) */
const PersonCard = ({ name, role, testId }) => (
  <div
    data-testid={testId}
    className="h-full bg-white border border-slate-100 p-5 sm:p-6 hover:border-slate-200 transition-colors"
  >
    <PhotoPlaceholder name={name} />
    <div className="mt-5">
      <h3 className="font-heading font-bold text-lg sm:text-xl text-[#0A192F] leading-tight">
        {name}
      </h3>
      {role ? (
        <p className="mt-1.5 text-sm text-slate-500 font-medium tracking-wide">
          {role}
        </p>
      ) : null}
    </div>
  </div>
);

const Team = () => {
  const { lang } = useLang();
  useSEO({
    title: lang === "en"
      ? "Our Team — Meet the Revelo Youth"
      : "我們的團隊 — 認識 Revelo 青年成員",
    description: lang === "en"
      ? "Meet the student leaders and youth volunteers behind Revelo's anti-scam education work in Hong Kong."
      : "認識 Revelo 推動香港反詐騙教育的學生領袖及青年義工團隊。",
    lang,
  });
  const tr = t[lang];

  return (
    <div data-testid="team-page">
      <PageHeader
        eyebrow={lang === "en" ? "Our team" : "團隊"}
        title={tr.team.title}
        intro={tr.team.intro}
        heroImage="https://customer-assets.emergentagent.com/job_youth-care-hk/artifacts/uk20r5f4_compress.png"
      />

      {/* 1. Founding Team */}
      <section className="py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A192F]">
              {tr.team.leadershipTitle}
            </h2>
          </Reveal>
          <StaggerGroup className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {tr.team.leadership.map((p, i) => (
              <StaggerItem key={i}>
                <PersonCard name={p.name} testId={`founder-${i}`} />
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* 2. Executive Team 2025–2026 */}
      <section className="py-12 lg:py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A192F]">
              {tr.team.executiveTitle}
            </h2>
          </Reveal>
          <StaggerGroup className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {tr.team.leadership.map((p, i) => (
              <StaggerItem key={i}>
                <PersonCard
                  name={p.name}
                  role={p.role}
                  testId={`executive-${i}`}
                />
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* 3. Leadership Team 2025–2026 */}
      <section className="py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A192F]">
              {tr.team.directorsTitle}
            </h2>
          </Reveal>

          <div className="mt-12 space-y-16 lg:space-y-20">
            {tr.team.departments.map((d, deptIdx) => (
              <Reveal key={deptIdx} delay={deptIdx * 0.04}>
                <div data-testid={`department-${deptIdx}`}>
                  <div className="flex items-center gap-3">
                    <span className="h-2 w-2 rounded-full bg-[#E9A229]" />
                    <h3 className="font-heading font-bold text-xl sm:text-2xl text-[#0A192F]">
                      {d.name}
                    </h3>
                  </div>
                  <p className="mt-3 text-slate-600 text-[15px] leading-relaxed max-w-3xl">
                    {d.description}
                  </p>

                  <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                    {d.members.map((m, mIdx) => (
                      <PersonCard
                        key={mIdx}
                        name={m}
                        role={lang === "en" ? "Director" : "負責人"}
                        testId={`director-${deptIdx}-${mIdx}`}
                      />
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Team;
